import React, { ReactElement, useRef } from 'react';

import { Icon, Portal } from '@sravni/react-design-system';
import { Fade } from '@sravni/react-design-system/lib/Transition';
import { Tooltip } from '@sravni/react-icons';
import { useBoolean, usePositioner, useStyles } from '@sravni/react-utils';

import type { Modifier } from '@popperjs/core';

import styles from './tooltip.module.scss';

interface IProps {
  children: ReactElement;
  icon?: ReactElement;
  width?: string;
}

export const CustomTooltip: React.FC<IProps> = ({ children, icon = <Tooltip />, width }) => {
  const cx = useStyles(styles);
  const reference = useRef<Element | null>(null);
  const popper = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useBoolean(false);

  const minWidthModifier: Modifier<'minWidth', any> = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.minWidth = '280px';
    },
  };

  const { referenceRef, popperRef, arrowRef, getReferenceProps, getPopperProps } = usePositioner({
    placement: 'bottom-start',
    modifiers: [
      minWidthModifier,
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'right', 'bottom', 'left'],
        },
      },
    ],
  });

  React.useEffect(() => {
    // @ts-ignore
    let timer = null;
    const listener = (event: Event) => {
      // @ts-ignore
      if (!visible && reference.current?.contains(event.target)) {
        setVisible.on();
      }

      // @ts-ignore
      if (visible && !reference.current?.contains(event.target) && !popper.current?.contains(event.target)) {
        timer = setTimeout(() => {
          setVisible.off();
        }, 50);
      }
    };

    document.addEventListener('mouseover', listener);

    return () => {
      // @ts-ignore
      timer && clearTimeout(timer);
      document.removeEventListener('mouseover', listener);
    };
  }, [reference, popper, setVisible, visible]);

  return (
    <div>
      <div ref={referenceRef} {...getReferenceProps({}, reference)} className={styles.iconWrapper}>
        <Icon icon={icon} />
      </div>
      <Portal>
        <Fade
          ref={popperRef}
          className={cx('bubble')}
          visible={visible}
          unmountOnExit
          {...getPopperProps({ style: { zIndex: 1060, maxWidth: width || '290px' } }, popper)}
        >
          <div ref={arrowRef} className={cx('arrow')} />
          <div className={styles.content}>{children}</div>
        </Fade>
      </Portal>
    </div>
  );
};
